// =====================================================
// SUBSPECIALTIES API CLIENT
// =====================================================

import { api } from './client';
import type {
  Subspecialty,
  SubspecialtySelect,
  CreateSubspecialtyRequest,
  UpdateSubspecialtyRequest,
} from '../types/catalog-admin';
import type { PaginationQuery, PaginatedResponse } from '../types/pagination';

const BASE_URL = '/subspecialties';

export const subspecialtiesApi = {
  /**
   * Listar todas (sin paginación - mantener compatibilidad)
   */
  getAll: async (includeInactive = false, specialtyId?: string): Promise<Subspecialty[]> => {
    const params = new URLSearchParams();
    if (includeInactive) params.set('includeInactive', 'true');
    if (specialtyId) params.set('specialtyId', specialtyId);
    const query = params.toString();
    return api.get<Subspecialty[]>(`${BASE_URL}${query ? `?${query}` : ''}`);
  },

  /**
   * Listar con paginación
   */
  getAllPaginated: async (params: PaginationQuery = {}, specialtyId?: string): Promise<PaginatedResponse<Subspecialty>> => {
    const searchParams = new URLSearchParams();
    
    if (params.page) searchParams.set('page', params.page.toString());
    if (params.limit) searchParams.set('limit', params.limit.toString());
    if (params.search) searchParams.set('search', params.search);
    if (params.sortBy) searchParams.set('sortBy', params.sortBy);
    if (params.sortOrder) searchParams.set('sortOrder', params.sortOrder);
    if (params.includeInactive) searchParams.set('includeInactive', 'true');
    if (specialtyId) searchParams.set('specialtyId', specialtyId);

    const queryString = searchParams.toString();
    const url = `${BASE_URL}/paginated${queryString ? `?${queryString}` : ''}`;
    
    return api.get<PaginatedResponse<Subspecialty>>(url);
  },

  /**
   * Obtener lista para select (solo activas)
   */
  getForSelect: async (specialtyId?: string): Promise<SubspecialtySelect[]> => {
    const query = specialtyId ? `?specialtyId=${specialtyId}` : '';
    return api.get<SubspecialtySelect[]>(`${BASE_URL}/select${query}`);
  },

  /**
   * Obtener por ID
   */
  getById: async (id: string): Promise<Subspecialty> => {
    return api.get<Subspecialty>(`${BASE_URL}/${id}`);
  },

  /**
   * Crear
   */
  create: async (data: CreateSubspecialtyRequest): Promise<Subspecialty> => {
    return api.post<Subspecialty>(BASE_URL, data);
  },

  /**
   * Actualizar
   */
  update: async (id: string, data: UpdateSubspecialtyRequest): Promise<Subspecialty> => {
    return api.patch<Subspecialty>(`${BASE_URL}/${id}`, data);
  },

  /**
   * Desactivar (soft delete)
   */
  deactivate: async (id: string): Promise<Subspecialty> => {
    return api.delete<Subspecialty>(`${BASE_URL}/${id}`);
  },

  /**
   * Reactivar
   */
  reactivate: async (id: string): Promise<Subspecialty> => {
    return api.patch<Subspecialty>(`${BASE_URL}/${id}/reactivate`);
  },
};

export default subspecialtiesApi;
